import updateProductInfoProductDetails from "@iewc/Store/Handlers/ProductDetails/UpdateProductInfo";
import updateProductInfoProductList from "@iewc/Store/Handlers/ProductList/UpdateProductInfo";
import { CustomState } from "@iewc/Store/Reducers";
import { colorStrings, mediaQueriesBP } from "@iewc/Utilities/variables";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export interface OwnProps {
    numberOfSpools: Array<number>;
    spoolsLength: number;
    setSelectedNumberOfSpools: (value: string) => void;
    selectedNumberOfSpools: string;
    uom: string;
    qtyOrdered: number;
    productId: string;
}

const mapStateToProps = (state: CustomState) => {
    const page = getCurrentPage(state);
    const pageType = page?.type;
    return {
        pageType,
        isLoadingPrice: state.customIsLoadingPrice.isLoadingPrice,
    };
};

const mapDispatchToProps = {
    updateProductInfoProductDetails,
    updateProductInfoProductList,
};

export type Props = OwnProps &
    ResolveThunks<typeof mapDispatchToProps> &
    ReturnType<typeof mapStateToProps> &
    HasShellContext;

export interface SpoolSelectorStyles {
    spoolsContainer?: GridContainerProps;
    numberOfSpoolsGridItem?: GridItemProps;
    separatorGridItem?: GridItemProps;
    spoolLengthGridItem?: GridItemProps;
    numberOfSpools?: DynamicDropdownPresentationProps;
    spoolLength?: TextFieldProps;
    tooltipWrapper?: InjectableCss;
    tooltipContent?: InjectableCss;
    tooltipStyles?: TooltipPresentationProps;
}

export const spoolSelectorStyles: SpoolSelectorStyles = {
    spoolsContainer: {
        gap: 20,
        css: css`
            margin-block: 5px 10px;
        `,
        offsetCss: css`
            align-items: flex-end;
        `,
    },

    numberOfSpoolsGridItem: {
        width: [5, 5, 5, 5, 5],
        css: css`
            padding-inline-end: 0;
        `,
    },

    separatorGridItem: {
        width: [2, 2, 2, 2, 2],
        css: css`
            padding-inline: 0;
            text-align: center;
            align-items: center;
            justify-content: center;
            padding-top: 25px;
            min-block-size: 80px;
        `,
    },

    spoolLengthGridItem: {
        width: [5, 5, 5, 5, 5],
        css: css`
            padding-inline-start: 0;
        `,
    },
    numberOfSpools: {
        mergeCss: true,
        labelProps: {
            size: 10,
            weight: 600,
            color: colorStrings.textMain,
            css: css`
                margin-bottom: -15px;
                align-items: center;
                display: flex;
                line-height: normal;
                white-space: break-spaces;
            `,
        },
        cssOverrides: {
            formField: css`
                gap: 5px;
            `,
        },
    },
    spoolLength: {
        mergeCss: true,
        css: css`
            input:not(.react-datetime-picker__inputGroup__input):disabled,
            &:disabled {
                border: 1px solid ${getColor(colorStrings.commonGlobalGray03)} !important;
                background: ${getColor(colorStrings.commonGlobalGray05)} !important;
                color: ${getColor(colorStrings.textMain)} !important;

                -webkit-text-fill-color: ${getColor(colorStrings.textMain)} !important;
                -webkit-opacity: 1;

                ${mediaQueriesBP.minMD} {
                    border: 1px solid ${getColor(colorStrings.commonGlobalGray03)} !important;
                    background: ${getColor(colorStrings.commonGlobalGray05)} !important;
                    color: ${getColor(colorStrings.textMain)} !important;
                }
            }
        `,
        cssOverrides: {
            formField: css`
                gap: 5px;
            `,
        },

        labelProps: {
            css: css`
                margin-bottom: -15px;
                &&& {
                    color: ${getColor(colorStrings.textMain)};
                    font-weight: 600;
                    font-size: 10px;
                    align-items: center;
                    display: flex;
                    line-height: normal;
                    white-space: break-spaces;
                }
            `,
        },
    },

    tooltipStyles: {
        cssOverrides: {
            tooltipBody: css`
                width: 100% !important;
            `,
        },
    },

    tooltipContent: {
        css: css`
            font-size: 12px;
            line-height: 16px;
            color: ${getColor(colorStrings.textMain)} !important;
        `,
    },
};

export const styles = spoolSelectorStyles;

const SpoolSelector = ({
    numberOfSpools,
    spoolsLength,
    setSelectedNumberOfSpools,
    selectedNumberOfSpools,
    uom,
    qtyOrdered,
    productId,
    updateProductInfoProductDetails,
    updateProductInfoProductList,
    pageType,
    isLoadingPrice,
    shellContext,
}: Props) => {
    const [options, setOptions] = useState<OptionObject[]>([]);
    const { isInShell } = shellContext;
    const isPageDetail = pageType === "ProductDetailsPage";
    const isPageList = pageType === "ProductListPage";
    const [localQtyOrdered, setLocalQtyOrdered] = useState(qtyOrdered);

    useEffect(() => {
        if (localQtyOrdered !== qtyOrdered) {
            setSelectedNumberOfSpools("1");
            setLocalQtyOrdered(qtyOrdered);
        }
    }, [qtyOrdered]);

    if (isInShell && isPageDetail) {
        return (
            <StyledWrapper
                css={css`
                    display: flex;
                    justify-content: center;
                `}
            >
                <Typography
                    css={css`
                        font-size: 16px;
                        color: #000;
                    `}
                >
                    Spool Selector Edit Mode
                </Typography>
            </StyledWrapper>
        );
    }

    useEffect(() => {
        const newOptions: OptionObject[] = [];
        numberOfSpools.forEach(spool => {
            newOptions.push({ optionValue: spool.toString(), optionText: spool.toString() });
        });
        setOptions(newOptions);
    }, [numberOfSpools]);

    const onSelectionChangeHandler = (value: string) => {
        setSelectedNumberOfSpools(value);

        if (isPageDetail) {
            updateProductInfoProductDetails({
                productId,
                qtyOrdered: qtyOrdered ?? 1,
                unitOfMeasure: uom,
                numberOfSpools: parseInt(value, 10) ?? 1,
            });
        }

        if (isPageList) {
            updateProductInfoProductList({
                productId,
                qtyOrdered: qtyOrdered ?? 1,
                unitOfMeasure: uom,
                numberOfSpools: parseInt(value, 10) ?? 1,
            });
        }
    };

    return (
        <GridContainer {...styles.spoolsContainer}>
            <GridItem {...styles.numberOfSpoolsGridItem}>
                <DynamicDropdown
                    {...styles.numberOfSpools}
                    selected={selectedNumberOfSpools}
                    onSelectionChange={(value?: string) => onSelectionChangeHandler(value || "")}
                    options={options}
                    disabled={isLoadingPrice}
                    label={
                        <>
                            {translate("Number of Spools")}
                            <Tooltip
                                {...styles.tooltipStyles}
                                text={
                                    (
                                        <StyledWrapper {...styles.tooltipWrapper}>
                                            <Typography {...styles.tooltipContent}>
                                                {siteMessage("ProductDetail_SpoolsTooltip")}
                                            </Typography>
                                        </StyledWrapper>
                                    ) as any
                                }
                            />
                        </>
                    }
                />
            </GridItem>
            <GridItem {...styles.separatorGridItem}>
                <Typography>x</Typography>
            </GridItem>
            <GridItem {...styles.spoolLengthGridItem}>
                <TextField
                    label={
                        <>
                            {translate("Spool Length")}
                            <Tooltip
                                {...styles.tooltipStyles}
                                text={
                                    (
                                        <StyledWrapper {...styles.tooltipWrapper}>
                                            <Typography {...styles.tooltipContent}>
                                                {siteMessage("ProductDetail_SpoolsLengthTooltip")}
                                            </Typography>
                                        </StyledWrapper>
                                    ) as any
                                }
                            />
                        </>
                    }
                    value={`${spoolsLength.toLocaleString()} ${uom}`}
                    type="text"
                    data-test-selector="product_spoolsLength"
                    disabled={true}
                    {...styles.spoolLength}
                />
            </GridItem>
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withIsInShell(SpoolSelector) as any) as any;
